import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Thermometer, SprayCan, PenLine, CalendarPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/App";
import { toast } from "sonner";

const FREQ_LABELS = { daily: "Giornaliera", weekly: "Settimanale", monthly: "Mensile", yearly: "Annuale" };

export default function HaccpSettings() {
  const { authAxios } = useAuth();
  const [points, setPoints] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [signers, setSigners] = useState([]);
  const [settings, setSettings] = useState(null);
  const [newSigner, setNewSigner] = useState("");
  const [backfill, setBackfill] = useState({ date_from: "2026-01-01", date_to: "" });
  const [running, setRunning] = useState(false);
  const [newPoint, setNewPoint] = useState({ name: "", min_temp: "0", max_temp: "4" });
  const [newTask, setNewTask] = useState({ name: "", frequency: "daily" });

  const load = useCallback(async () => {
    try {
      const [p, t, s, st] = await Promise.all([
        authAxios.get("/haccp/points"),
        authAxios.get("/haccp/tasks"),
        authAxios.get("/haccp/signers"),
        authAxios.get("/haccp/settings"),
      ]);
      setPoints(p.data.items || []);
      setTasks(t.data.items || []);
      setSigners(s.data.items || []);
      setSettings(st.data);
    } catch { toast.error("Errore caricamento impostazioni"); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    try {
      const r = await authAxios.put("/haccp/settings", patch);
      setSettings(r.data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const addSigner = async () => {
    if (!newSigner.trim()) { toast.error("Inserisci il nome del firmatario"); return; }
    try {
      await authAxios.post("/haccp/signers", { name: newSigner });
      setNewSigner("");
      toast.success("Firmatario aggiunto");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const delSigner = async (id) => {
    try { await authAxios.delete(`/haccp/signers/${id}`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore eliminazione"); }
  };

  const runBackfill = async () => {
    if (!backfill.date_from) { toast.error("Indica la data di inizio"); return; }
    setRunning(true);
    const t = toast.loading("Generazione registro storico...");
    try {
      const r = await authAxios.post("/haccp/backfill", backfill, { timeout: 300000 });
      toast.success(`Registro completato: ${r.data.days} giorni`, { id: t, duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione registro", { id: t });
    } finally { setRunning(false); }
  };

  const savePointLimits = async (p, patch) => {
    try {
      await authAxios.put(`/haccp/points/${p.id}`, {
        name: patch.name ?? p.name,
        min_temp: parseFloat(String(patch.min_temp ?? p.min_temp).replace(",", ".")),
        max_temp: parseFloat(String(patch.max_temp ?? p.max_temp).replace(",", ".")),
        slots: p.slots,
        active: p.active,
      });
      toast.success("Punto di misura aggiornato");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const addPoint = async () => {
    if (!newPoint.name.trim()) { toast.error("Inserisci il nome del punto"); return; }
    try {
      await authAxios.post("/haccp/points", {
        name: newPoint.name,
        min_temp: parseFloat(String(newPoint.min_temp).replace(",", ".")),
        max_temp: parseFloat(String(newPoint.max_temp).replace(",", ".")),
        slots: ["mattina", "pomeriggio"],
      });
      setNewPoint({ name: "", min_temp: "0", max_temp: "4" });
      toast.success("Punto di misura aggiunto");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const delPoint = async (id) => {
    if (!window.confirm("Eliminare il punto di misura e le sue rilevazioni?")) return;
    try { await authAxios.delete(`/haccp/points/${id}`); await load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  const addTask = async () => {
    if (!newTask.name.trim()) { toast.error("Inserisci il nome dell'attività"); return; }
    try {
      await authAxios.post("/haccp/tasks", newTask);
      setNewTask({ name: "", frequency: "daily" });
      toast.success("Attività aggiunta");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
  };

  const delTask = async (id) => {
    if (!window.confirm("Eliminare l'attività e il suo storico?")) return;
    try { await authAxios.delete(`/haccp/tasks/${id}`); await load(); }
    catch { toast.error("Errore eliminazione"); }
  };

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="haccp-settings-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto pb-16">
        <PageHeader title="Impostazioni HACCP" />

        <Card className="bg-[#18181b] border-amber-500/20 mb-5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <PenLine className="w-4 h-4 text-amber-400" />
              <h3 className="text-white text-base font-semibold">Responsabile e firmatari</h3>
            </div>
            <label className="text-[11px] uppercase tracking-wide text-zinc-500">Responsabile</label>
            <Input
              key={settings?.responsible_name}
              data-testid="settings-responsible-input"
              defaultValue={settings?.responsible_name || ""}
              onBlur={(e) => e.target.value.trim() && e.target.value !== settings?.responsible_name && saveSettings({ responsible_name: e.target.value })}
              className="h-10 mt-1 mb-4 bg-[#0f0f11] border-white/10 text-white"
            />
            <label className="text-[11px] uppercase tracking-wide text-zinc-500">Firmatari</label>
            <div className="space-y-1.5 mt-1" data-testid="signers-list">
              {signers.map((s) => (
                <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg border border-white/10">
                  <span className="text-sm text-zinc-300 flex-1 min-w-0 truncate">{s.name}</span>
                  <Button size="sm" variant="ghost" onClick={() => delSigner(s.id)} data-testid={`signer-delete-${s.id}`} className="h-7 text-zinc-500 hover:text-red-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Input
                data-testid="new-signer-name"
                value={newSigner}
                placeholder="Nuovo firmatario"
                onChange={(e) => setNewSigner(e.target.value)}
                className="h-9 flex-1 bg-[#0f0f11] border-white/10 text-white"
              />
              <Button size="sm" onClick={addSigner} data-testid="new-signer-add" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <label className="block text-[11px] uppercase tracking-wide text-zinc-500 mt-4">Valori di riferimento cella (°C)</label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                key={`ref-min-${settings?.temp_default_min}`}
                data-testid="settings-temp-min"
                defaultValue={settings?.temp_default_min ?? ""}
                onBlur={(e) => Number(e.target.value) !== settings?.temp_default_min && saveSettings({ temp_default_min: parseFloat(String(e.target.value).replace(",", ".")) })}
                className="h-9 w-20 bg-[#0f0f11] border-white/10 text-white text-center"
              />
              <span className="text-zinc-500 text-xs">÷</span>
              <Input
                key={`ref-max-${settings?.temp_default_max}`}
                data-testid="settings-temp-max"
                defaultValue={settings?.temp_default_max ?? ""}
                onBlur={(e) => Number(e.target.value) !== settings?.temp_default_max && saveSettings({ temp_default_max: parseFloat(String(e.target.value).replace(",", ".")) })}
                className="h-9 w-20 bg-[#0f0f11] border-white/10 text-white text-center"
              />
              <span className="text-[11px] text-zinc-500">valore proposto nel registro</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-teal-500/20 mb-5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <CalendarPlus className="w-4 h-4 text-teal-400" />
              <h3 className="text-white text-base font-semibold">Registro storico</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-3">
              Genera il registro dei giorni passati (temperature e pulizie) fino a ieri. Le voci già presenti non vengono modificate.
            </p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Da</label>
                <Input
                  type="date"
                  data-testid="backfill-from"
                  value={backfill.date_from}
                  onChange={(e) => setBackfill({ ...backfill, date_from: e.target.value })}
                  className="h-10 w-36 bg-[#0f0f11] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">A (vuoto = ieri)</label>
                <Input
                  type="date"
                  data-testid="backfill-to"
                  value={backfill.date_to}
                  onChange={(e) => setBackfill({ ...backfill, date_to: e.target.value })}
                  className="h-10 w-36 bg-[#0f0f11] border-white/10 text-white"
                />
              </div>
              <Button onClick={runBackfill} disabled={running} data-testid="backfill-run-btn" className="h-10 bg-amber-500 hover:bg-amber-400 text-black">
                Genera
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-sky-500/20 mb-5">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Thermometer className="w-4 h-4 text-sky-400" />
              <h3 className="text-white text-base font-semibold">Punti di misura</h3>
            </div>            <div className="space-y-3" data-testid="points-list">
              {points.map((p) => (
                <div key={p.id} className="flex items-center gap-2 flex-wrap p-2.5 rounded-lg border border-white/10">
                  <span className="text-sm text-zinc-200 flex-1 min-w-[120px]">{p.name}</span>
                  <Input
                    defaultValue={p.min_temp}
                    data-testid={`point-min-${p.id}`}
                    onBlur={(e) => Number(e.target.value) !== p.min_temp && savePointLimits(p, { min_temp: e.target.value })}
                    className="h-9 w-16 bg-[#0f0f11] border-white/10 text-white text-center"
                  />
                  <span className="text-zinc-500 text-xs">÷</span>
                  <Input
                    defaultValue={p.max_temp}
                    data-testid={`point-max-${p.id}`}
                    onBlur={(e) => Number(e.target.value) !== p.max_temp && savePointLimits(p, { max_temp: e.target.value })}
                    className="h-9 w-16 bg-[#0f0f11] border-white/10 text-white text-center"
                  />
                  <span className="text-zinc-500 text-xs">°C · {(p.slots || []).length} rilevazioni/gg</span>
                  <Button size="sm" variant="ghost" onClick={() => delPoint(p.id)} data-testid={`point-delete-${p.id}`} className="h-8 text-zinc-500 hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Input
                data-testid="new-point-name"
                value={newPoint.name}
                placeholder="Nuovo punto (es. Cella 2)"
                onChange={(e) => setNewPoint({ ...newPoint, name: e.target.value })}
                className="h-9 flex-1 min-w-[140px] bg-[#0f0f11] border-white/10 text-white"
              />
              <Input data-testid="new-point-min" value={newPoint.min_temp} onChange={(e) => setNewPoint({ ...newPoint, min_temp: e.target.value })} className="h-9 w-16 bg-[#0f0f11] border-white/10 text-white text-center" />
              <Input data-testid="new-point-max" value={newPoint.max_temp} onChange={(e) => setNewPoint({ ...newPoint, max_temp: e.target.value })} className="h-9 w-16 bg-[#0f0f11] border-white/10 text-white text-center" />
              <Button size="sm" onClick={addPoint} data-testid="new-point-add" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#18181b] border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <SprayCan className="w-4 h-4 text-emerald-400" />
              <h3 className="text-white text-base font-semibold">Attività di pulizia</h3>
            </div>
            {Object.keys(FREQ_LABELS).map((f) => (
              <div key={f} className="mb-3">
                <p className="text-[11px] uppercase tracking-wide text-zinc-500 mb-1">{FREQ_LABELS[f]}</p>
                <div className="space-y-1.5">
                  {tasks.filter((t) => t.frequency === f).map((t) => (
                    <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg border border-white/10">
                      <span className="text-sm text-zinc-300 flex-1 min-w-0 truncate">{t.name}</span>
                      <Button size="sm" variant="ghost" onClick={() => delTask(t.id)} data-testid={`task-delete-${t.id}`} className="h-7 text-zinc-500 hover:text-red-400">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                  {!tasks.filter((t) => t.frequency === f).length && (
                    <p className="text-xs text-zinc-600">Nessuna attività.</p>
                  )}
                </div>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Input
                data-testid="new-task-name"
                value={newTask.name}
                placeholder="Nuova attività"
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                className="h-9 flex-1 min-w-[140px] bg-[#0f0f11] border-white/10 text-white"
              />
              <select
                data-testid="new-task-frequency"
                value={newTask.frequency}
                onChange={(e) => setNewTask({ ...newTask, frequency: e.target.value })}
                className="h-9 rounded-md bg-[#0f0f11] border border-white/10 text-white text-sm px-2"
              >
                {Object.keys(FREQ_LABELS).map((f) => <option key={f} value={f}>{FREQ_LABELS[f]}</option>)}
              </select>
              <Button size="sm" onClick={addTask} data-testid="new-task-add" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
